import { button } from './button.css';
import type { ButtonVariants } from './button.css';
import type { IconName } from '@customerio/pluma-icons';
import clsx from 'clsx';
import { legacyButtonOverride } from './button-legacy.css';
import type { LegacyButtonOverrideVariants } from './button-legacy.css';

export const buttonVariants = new Set(button.variants());
export * from './button.css';

export interface BaseButtonProps {
	variant?: 'primary' | 'secondary' | 'tertiary' | 'outline' | 'linkPrimary' | 'linkSecondary';
	size?: 'medium' | 'small';
	isError?: boolean;
	icon?: IconName;
	iconPosition?: 'leading' | 'trailing';
	isIconOnly?: boolean;
	isDisabled?: boolean;
	unsafe_isLegacy?: boolean;
}

// The component props don't map to the CSS props 100%, as the CSS side allows
// for more flexibility in terms of combining button shape and color.
// This maps the component props to the correct CSS styles.
const componentPropsToCssProps: Record<string, { variant: ButtonVariants['variant']; tone: ButtonVariants['tone'] }> = {
	primary: { variant: 'primary', tone: 'accent' },
	secondary: { variant: 'secondary', tone: 'default' },
	tertiary: { variant: 'tertiary', tone: 'default' },
	outline: { variant: 'outline', tone: 'default' },
	linkPrimary: { variant: 'link', tone: 'accent' },
	linkSecondary: { variant: 'link', tone: 'default' },
};

interface CssProps {
	variant: ButtonVariants['variant'];
	tone: ButtonVariants['tone'];
	icon?: ButtonVariants['icon'];
	size?: ButtonVariants['size'];
}

const DEFAULT_CSS_PROPS: CssProps = Object.freeze({
	variant: 'secondary',
	tone: 'default',
	size: 'medium',
	icon: undefined,
});

function calculateCssProps(props: Partial<BaseButtonProps>) {
	let cssProps = Object.assign({}, DEFAULT_CSS_PROPS);

	if (props.variant != null) {
		const mappedProps = componentPropsToCssProps[props.variant];

		if (mappedProps != null) {
			cssProps = Object.assign(cssProps, mappedProps);
		}
	}

	if (props.isError) {
		cssProps.tone = 'critical';
	}

	if (props.icon != null) {
		if (props.iconPosition === 'trailing') {
			cssProps.icon = 'trailing';
		} else {
			cssProps.icon = 'leading';
		}

		if (props.isIconOnly) {
			cssProps.icon = 'only';
		}
	}

	if (props.size != null) {
		cssProps.size = props.size;
	}

	return cssProps;
}

interface LegacyCssProps {
	variant?: LegacyButtonOverrideVariants['variant'];
	tone?: LegacyButtonOverrideVariants['tone'];
}
const SUPPORTED_LEGACY_CSS_PROPS = Object.freeze({
	variant: new Set(['secondary']),
	tone: new Set(['default', 'accent', 'critical']),
});

function legacyOverrideClasses(buttonCssProps: ReturnType<typeof calculateCssProps>) {
	const legacyCssProps: Partial<LegacyCssProps> = {};
	Object.entries(buttonCssProps).forEach(([key, value]) => {
		if (
			key in SUPPORTED_LEGACY_CSS_PROPS &&
			(SUPPORTED_LEGACY_CSS_PROPS[key as keyof LegacyCssProps]?.has(value) ?? false)
		) {
			legacyCssProps[key as keyof LegacyCssProps] = value;
		}
	});

	return legacyButtonOverride(legacyCssProps);
}

export function buttonClassName(props: Partial<BaseButtonProps>) {
	let cssProps = calculateCssProps(props);

	const classes = [button(cssProps)];

	if (props.unsafe_isLegacy === true) {
		classes.push(legacyOverrideClasses(cssProps));
	}

	return clsx(classes);
}
